import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AccountTypeSelector, JoinTeamForm, SignUpForm, ProgressBar, CRMConnect, AccountingConnect, ChartOfAccounts, Checkout } from '../components/Onboarding';
import { useAppContext } from '../context/AppContext';
import { handleStripeCheckoutCompletion } from '../services';

const sections = [
  { id: 'user-form-section', title: 'Profile' },
  { id: 'crm-section', title: 'CRM' },
  { id: 'acct-section', title: 'Accounting' },
  { id: 'chart-of-accounts-section', title: 'Accounts' },
  { id: 'checkout-section', title: 'Checkout' }
];

export const SignUpPage = () => {
  const { plan } = useParams();
  const { base_url, token, setToken, showStatus } = useAppContext();
  const [accountType, setAccountType] = useState(null);
  const [currentSection, setCurrentSection] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const urlParams = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params;
  }, []);

  useEffect(() => {
    let sectionToShow = 0;

    if (urlParams.has('coa') && urlParams.get('coa') === 'success') {
      setAccountType('company');
      sectionToShow = 4;
    } else if (urlParams.has('acct') && urlParams.get('acct') === 'success') {
      setAccountType('company');
      sectionToShow = 3;
    } else if (urlParams.has('crm') && urlParams.get('crm') === 'success') {
      setAccountType('company');
      sectionToShow = 2;
    } else if (urlParams.has('profile') && urlParams.get('profile') === 'success') {
      setAccountType('company');
      sectionToShow = 1;
    }

    setCurrentSection(sectionToShow);
    setIsLoading(false);
  }, [base_url, urlParams]);

  useEffect(() => {
    if (urlParams.has('rt')) {
      setToken(urlParams.get('rt'));
    }
  }, [urlParams, setToken]);

  useEffect(() => {
    const stripeLoading = async () => {
      const sessionId = urlParams.get('session_id');

      if (sessionId) {
        const isComplete = await handleStripeCheckoutCompletion(sessionId, base_url);
        if (isComplete) {
          const tokenToUse = urlParams.get('rt') || token;
          window.location.href = `/?token=${tokenToUse}&first_time=true`;
        }
      }
    };

    stripeLoading();
  }, [base_url, token, urlParams]);

  const handleSelectAccountType = (type) => {
    setAccountType(type);
  };

  const handleJoinTeamSuccess = () => {
    navigate('/?first_time=true');
  };

  const handleNext = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
    }
  };

  const handleError = (message) => {
    showStatus('error', message);
  };

  const handleBack = () => {
    setAccountType(null);
    setCurrentSection(0);
  };

  if (isLoading) {
    return (
      <div className="onboarding-wrapper">
        <div className="onboarding-init-loader">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-wrapper">
      <div className="onboarding-card">
        {accountType === 'company' && (
          <ProgressBar currentStep={currentSection + 1} totalSteps={sections.length} />
        )}

        {!accountType ? (
          <AccountTypeSelector onSelectType={handleSelectAccountType} />
        ) : accountType === 'team' ? (
          <JoinTeamForm
            onSuccess={handleJoinTeamSuccess}
            onError={handleError}
            onBack={handleBack}
          />
        ) : (
          <>
            {currentSection === 0 && <SignUpForm onSuccess={handleNext} onError={handleError} />}
            {currentSection === 1 && <CRMConnect onSuccess={handleNext} />}
            {currentSection === 2 && <AccountingConnect onSuccess={handleNext} />}
            {currentSection === 3 && <ChartOfAccounts onSuccess={handleNext} onError={handleError} />}
            {currentSection === 4 && <Checkout plan={plan} />}
          </>
        )}
      </div>
    </div>
  );
};
